import { useState } from 'react';
import {
    Chip,
    Collapse,
    Grid,
    Typography,
    Box,
    IconButton,
    Button,
    Paper,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FormTextField from '../../components/FormFields/FormTextField';
import { useAddNewPersonMutation } from '../../api/peopleApiSlice';
import UpdateButton from './adminUtils/updateButton';
import HenkiloImageUploader from '../../features/events/components/EventForm/ImageUploader/HenkiloImageUploader';
import SearchBar from './adminUtils/SearchBar';
import FormSelectMultiple from '../../features/events/components/EventForm/FormSelectMultiple';
import { useGetYhteystiedotQuery } from '../../api/yhteystiedotApiSlice';
import PaginationInfo from './PaginationInfo';

const EMPTY_PERSON = {
    nimi: '',
    email: '',
    titteli: '',
    puhelinnumero: '',
    puhelinnumero2: '',
    yhteystiedot: [],
    henkilokuva: null,
    tyonkuva: '',
    liity_lahettajaksi: '',
};

function PeopleList({ people, handleDeletePerson, handleUpdatePerson, fetchData, onSuccess, onError }) {
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [newPersonData, setNewPersonData] = useState(EMPTY_PERSON);
    const [addPersonData, setAddPersonData] = useState(EMPTY_PERSON);
    const [showAddForm, setShowAddForm] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedYhteystieto, setSelectedYhteystieto] = useState('');
    const itemsPerPage = 10;
    const [addNewPerson, { isLoading }] = useAddNewPersonMutation();
    const { data: yhteystiedot, isLoading: yhteystiedotLoading } = useGetYhteystiedotQuery();
    const yhteystietoArray = yhteystiedot?.entities
        ? Object.values(yhteystiedot.entities).map((item) => item.yhteystieto)
        : [];

    const handleUpdateNewPersonData = (selectedItems) => {
        setNewPersonData((prevState) => ({ ...prevState, yhteystiedot: selectedItems }));
    };

    const handleUpdateAddPersonData = (selectedItems) => {
        setAddPersonData((prevState) => ({ ...prevState, yhteystiedot: selectedItems }));
    };

    const handleSearchChange = (newSearchTerm) => {
        setSearchTerm(newSearchTerm);
        setCurrentPage(1);
    };

    const filteredPeople = Array.isArray(people)
        ? people.filter((person) => {
            const matchesName = person.nimi.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesYhteystieto =
                selectedYhteystieto === '' ||
                (person.yhteystiedot && person.yhteystiedot.includes(selectedYhteystieto));
            return matchesName && matchesYhteystieto;
        })
        : [];
    const totalPages = Math.ceil(filteredPeople.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const displayedPeople = filteredPeople.slice(startIndex, endIndex);

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleUserClick = (person) => {
        setShowAddForm(false);
        setAddPersonData(EMPTY_PERSON);

        if (selectedPerson?.id === person.id) {
            setSelectedPerson(null);
            setNewPersonData(EMPTY_PERSON);
        } else {
            setSelectedPerson(person);
            setNewPersonData(person);
        }
    };

    const handleAddClick = () => {
        setShowAddForm(!showAddForm);
        setSelectedPerson(null);
        setNewPersonData(EMPTY_PERSON);
    };

    const handleImageChange = (imageData) => {
        if (selectedPerson) {
            setNewPersonData((prevState) => ({ ...prevState, henkilokuva: imageData }));
        } else {
            setAddPersonData((prevState) => ({ ...prevState, henkilokuva: imageData }));
        }
    };

    const handleLisääPerson = async (person) => {
        try {
            const result = await addNewPerson(person);
            if (result.error) onError('Henkilön luominen epäonnistui');
            if (!result.error) onSuccess('Henkilön luominen onnistui.', 'henkilot', result.data.id);
            setAddPersonData(EMPTY_PERSON);
            setShowAddForm(false);
            fetchData();
        } catch (error) {
            console.error(error);
            onError('Henkilön luominen epäonnistui');
        }
    };

    return (
        <Box sx={{ py: 3 }}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Henkilöt
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <SearchBar searchTerm={searchTerm} onSearchChange={handleSearchChange} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl sx={{ width: '100%' }} disabled={yhteystiedotLoading}>
                            <InputLabel id="yhteystieto-filter-label">Yhteystieto</InputLabel>
                            <Select
                                labelId="yhteystieto-filter-label"
                                id="yhteystieto-filter-select"
                                value={selectedYhteystieto}
                                label="Yhteystieto"
                                onChange={(event) => {
                                    setSelectedYhteystieto(event.target.value);
                                    setCurrentPage(1);
                                }}
                            >
                                <MenuItem value="">Kaikki yhteystiedot</MenuItem>
                                {yhteystietoArray.map((yhteystieto) => (
                                    <MenuItem key={yhteystieto} value={yhteystieto}>
                                        {yhteystieto}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                {displayedPeople.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                        Ei henkilöitä näytettäväksi.
                    </Typography>
                ) : (
                    displayedPeople.map((person) => {
                        const isSelected = selectedPerson?.id === person.id;
                        const summaryText = [person.titteli, person.email || person.puhelinnumero]
                            .filter(Boolean)
                            .join(' | ');

                        return (
                            <Box key={person.id} sx={{ mb: 1.5 }}>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: isSelected ? 'taphaBlue.main' : 'grey.200',
                                        bgcolor: isSelected ? 'rgba(25, 118, 210, 0.06)' : 'taphaWhite.main',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        '&:hover': {
                                            bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'grey.50',
                                            boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                                            cursor: 'pointer',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                    onClick={() => handleUserClick(person)}
                                >
                                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {person.nimi}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                            {summaryText || 'Ei lisätietoja'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                                            {person.puhelinnumero && (
                                                <Chip size="small" variant="outlined" label={person.puhelinnumero} />
                                            )}
                                            {person.yhteystiedot?.slice(0, 2).map((item) => (
                                                <Chip key={item} size="small" label={item} />
                                            ))}
                                            {person.yhteystiedot?.length > 2 && (
                                                <Chip size="small" variant="outlined" label={`+${person.yhteystiedot.length - 2} lisää`} />
                                            )}
                                        </Box>
                                    </Box>
                                    <IconButton
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleDeletePerson(event, person);
                                            if (selectedPerson?.id === person.id) {
                                                setSelectedPerson(null);
                                                setNewPersonData(EMPTY_PERSON);
                                            }
                                        }}
                                        sx={{ color: 'error.main', flexShrink: 0 }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>

                                <Collapse in={isSelected} timeout="auto" unmountOnExit>
                                    <Box sx={{ mt: 1, p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                            Muokkaa henkilöä
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <FormTextField
                                                    fieldName="nimi"
                                                    label="Nimi"
                                                    multiline={true}
                                                    fullWidth={true}
                                                    value={newPersonData.nimi}
                                                    onChange={(event) => {
                                                        const { name, value } = event.target;
                                                        setNewPersonData((prevState) => ({ ...prevState, [name]: value }));
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormTextField
                                                    fieldName="email"
                                                    label="Email"
                                                    multiline={true}
                                                    fullWidth={true}
                                                    value={newPersonData.email}
                                                    onChange={(event) => {
                                                        const { name, value } = event.target;
                                                        setNewPersonData((prevState) => ({ ...prevState, [name]: value }));
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormTextField
                                                    fieldName="titteli"
                                                    label="Titteli"
                                                    multiline={true}
                                                    fullWidth={true}
                                                    value={newPersonData.titteli}
                                                    onChange={(event) => {
                                                        const { name, value } = event.target;
                                                        setNewPersonData((prevState) => ({ ...prevState, [name]: value }));
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormTextField
                                                    fieldName="puhelinnumero"
                                                    label="Puhelinnumero"
                                                    multiline={true}
                                                    fullWidth={true}
                                                    value={newPersonData.puhelinnumero}
                                                    onChange={(event) => {
                                                        const { name, value } = event.target;
                                                        setNewPersonData((prevState) => ({ ...prevState, [name]: value }));
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormTextField
                                                    fieldName="puhelinnumero2"
                                                    label="2. Puhelinnumero"
                                                    multiline={true}
                                                    fullWidth={true}
                                                    value={newPersonData.puhelinnumero2}
                                                    onChange={(event) => {
                                                        const { name, value } = event.target;
                                                        setNewPersonData((prevState) => ({ ...prevState, [name]: value }));
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormTextField
                                                    fieldName="tyonkuva"
                                                    label="Työnkuva"
                                                    multiline={true}
                                                    fullWidth={true}
                                                    value={newPersonData.tyonkuva}
                                                    onChange={(event) => {
                                                        const { name, value } = event.target;
                                                        setNewPersonData((prevState) => ({ ...prevState, [name]: value }));
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormTextField
                                                    fieldName="liity_lahettajaksi"
                                                    label="Liity lähettäjäksi"
                                                    multiline={true}
                                                    fullWidth={true}
                                                    value={newPersonData.liity_lahettajaksi}
                                                    onChange={(event) => {
                                                        const { name, value } = event.target;
                                                        setNewPersonData((prevState) => ({ ...prevState, [name]: value }));
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormSelectMultiple
                                                    fieldName="yhteystiedot"
                                                    label="Yhteystiedot"
                                                    existingYhteystiedot={newPersonData.yhteystiedot}
                                                    onUpdate={handleUpdateNewPersonData}
                                                    options={yhteystietoArray}
                                                    multiline={true}
                                                    fullWidth={true}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                    Henkilökuva
                                                </Typography>
                                                <HenkiloImageUploader
                                                    fieldName="henkilokuva"
                                                    onImageChange={handleImageChange}
                                                    image={newPersonData.henkilokuva}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <UpdateButton handleUpdate={handleUpdatePerson} data={newPersonData} />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Collapse>
                            </Box>
                        );
                    })
                )}

                <Box sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={showAddForm ? <CloseIcon /> : <AddIcon />}
                        onClick={handleAddClick}
                        sx={{
                            bgcolor: showAddForm ? 'grey.600' : 'taphaBlue.main',
                            '&:hover': { bgcolor: showAddForm ? 'grey.700' : 'taphaBlue.dark' },
                        }}
                    >
                        {showAddForm ? 'Sulje' : 'Lisää henkilö'}
                    </Button>
                    {showAddForm && (
                        <Box
                            sx={{
                                mt: 2,
                                p: 2,
                                border: '1px solid',
                                borderColor: 'grey.200',
                                borderRadius: 1,
                                bgcolor: 'grey.50',
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                Lisää uusi henkilö
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormTextField
                                        key="addPersonNimi"
                                        fieldName="nimi"
                                        label="Nimi"
                                        multiline={true}
                                        fullWidth={true}
                                        value={addPersonData.nimi}
                                        onChange={(event) =>
                                            setAddPersonData({ ...addPersonData, nimi: event.target.value })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormTextField
                                        key="addPersonEmail"
                                        fieldName="email"
                                        label="Email"
                                        multiline={true}
                                        fullWidth={true}
                                        value={addPersonData.email}
                                        onChange={(event) =>
                                            setAddPersonData({ ...addPersonData, email: event.target.value })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormTextField
                                        key="addPersonTitteli"
                                        fieldName="titteli"
                                        label="Titteli"
                                        multiline={true}
                                        fullWidth={true}
                                        value={addPersonData.titteli}
                                        onChange={(event) =>
                                            setAddPersonData({ ...addPersonData, titteli: event.target.value })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormTextField
                                        key="addPersonPuhelinnumero"
                                        fieldName="puhelinnumero"
                                        label="Puhelinnumero"
                                        multiline={true}
                                        fullWidth={true}
                                        value={addPersonData.puhelinnumero}
                                        onChange={(event) =>
                                            setAddPersonData({ ...addPersonData, puhelinnumero: event.target.value })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormTextField
                                        key="addPersonPuhelinnumero2"
                                        fieldName="puhelinnumero2"
                                        label="2. Puhelinnumero"
                                        multiline={true}
                                        fullWidth={true}
                                        value={addPersonData.puhelinnumero2}
                                        onChange={(event) =>
                                            setAddPersonData({ ...addPersonData, puhelinnumero2: event.target.value })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormTextField
                                        key="addPersonTyonkuva"
                                        fieldName="tyonkuva"
                                        label="Työnkuva"
                                        multiline={true}
                                        fullWidth={true}
                                        value={addPersonData.tyonkuva}
                                        onChange={(event) =>
                                            setAddPersonData({ ...addPersonData, tyonkuva: event.target.value })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormTextField
                                        key="addPersonLiityLahettajaksi"
                                        fieldName="liity_lahettajaksi"
                                        label="Liity lähettäjäksi"
                                        multiline={true}
                                        fullWidth={true}
                                        value={addPersonData.liity_lahettajaksi}
                                        onChange={(event) =>
                                            setAddPersonData({ ...addPersonData, liity_lahettajaksi: event.target.value })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormSelectMultiple
                                        key="addPersonYhteystiedot"
                                        fieldName="yhteystiedot"
                                        label="Yhteystiedot"
                                        existingYhteystiedot={addPersonData.yhteystiedot}
                                        onUpdate={handleUpdateAddPersonData}
                                        options={yhteystietoArray}
                                        multiline={true}
                                        fullWidth={true}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Henkilökuva
                                    </Typography>
                                    <HenkiloImageUploader
                                        fieldName="henkilokuva"
                                        onImageChange={handleImageChange}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={isLoading ? null : <AddIcon />}
                                        onClick={() => handleLisääPerson(addPersonData)}
                                        disabled={isLoading}
                                        sx={{ bgcolor: 'taphaGreen.main', '&:hover': { bgcolor: 'taphaGreen.dark' } }}
                                    >
                                        {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Luo'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </Box>

                {filteredPeople.length > itemsPerPage && (
                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <PaginationInfo
                            currentPage={currentPage}
                            itemsPerPage={itemsPerPage}
                            filteredArray={filteredPeople}
                            array={people}
                        />
                        <Box sx={{ mt: 1 }}>
                            <IconButton
                                size="small"
                                onClick={handlePreviousPage}
                                disabled={currentPage <= 1}
                                sx={{ mr: 1 }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={handleNextPage}
                                disabled={currentPage >= totalPages}
                            >
                                <ArrowForwardIcon />
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}

export default PeopleList;